import * as Api from '@ViewModels';
import { FavoriteFor, TagViewModel } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IModalContext, ModalChildComponentContextKey } from '../../../../../models';
import { useEventLogging } from '../../../../../models/Logging';
import { useCreateTag } from '../../../../../queries';
import { Column, Row } from '../../../../components-v2/PageLayout';
import { ThemeColors } from '../../../../components-v2/Theme/theme';
import { ColorPicker } from '../../../../containers/PeopleV2/TagsContainer/ColorPicker/ColorPicker';
import { baseStyleSheet } from '../../../../styles/styles';
import { toSentenceCase } from '../../../../utils/toSentenceCase';
import { DeprecatedCloseButton } from '../../../DeprecatedCloseButton';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { IModalProps, Modal } from '../../../Modal';
import { TextInput } from '../../../TextInput';
import { styleSheet } from './styles';

interface IProps extends IModalContext<TagViewModel> {
	impersonationContext?: Api.IImpersonationContext;
}

function _AddTag({ parentModal, impersonationContext }: IProps) {
	const { logInput, logApiError } = useEventLogging('AddTag');
	const [errorMessage, setErrorMessage] = React.useState<string>();
	const [tagValue, setTagValue] = React.useState<string>('');
	const [tagColor, setTagColor] = React.useState<keyof ThemeColors['pill']>('gray');
	const addTagMutation = useCreateTag({
		impersonationContext,
		onError: error => {
			logApiError('Add-Error', error);
			setErrorMessage(error.systemMessage);
		},
		onSuccess: data => {
			onRequestClose(data, false);
		},
	});
	const onRequestClose = (result?: TagViewModel, cancel = false) => {
		parentModal?.onRequestClose(result, cancel);
	};
	const onCloseClicked = () => {
		onRequestClose(null, true);
	};
	const onTagValueChanged = (ev: React.ChangeEvent<HTMLInputElement>) => {
		setErrorMessage(null);
		setTagValue(ev.target.value);
	};
	const onTagValueTextInputFocus = () => {
		setErrorMessage(null);
	};
	const onAddButtonClicked = () => {
		const value = tagValue.trim();
		if (!value) {
			setErrorMessage('Please enter a value for this tag');
			return;
		}
		logInput('Add', 'Click');
		addTagMutation.mutate({
			accountTag: {
				favoriteFor: FavoriteFor.None,
				name: value,
				color: toSentenceCase(tagColor) as keyof ThemeColors['pill'],
			},
		});
	};
	const onTagValueTextInputKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
		if (ev.key === 'Enter') {
			onAddButtonClicked();
		}
	};
	return (
		<Column styles={[styleSheet.addTag]}>
			<Row styles={[styleSheet.addTagHeader]}>
				<DeprecatedCloseButton disabled={addTagMutation.isPending} onClick={onCloseClicked} />
			</Row>
			<Row>
				<span className={css(styleSheet.addTagTitle)}>Add Tag</span>
			</Row>
			<Row styles={[styleSheet.inputRow]} fullWidth>
				<TextInput
					autoFocus={true}
					className={css(styleSheet.addTagTextInput)}
					disabled={addTagMutation.isPending}
					inputId='add-tag-input'
					onChange={onTagValueChanged}
					onFocus={onTagValueTextInputFocus}
					onKeyDown={onTagValueTextInputKeyDown}
					placeholder='Enter the new tag here'
					type='text'
					value={tagValue}
				/>
				<ColorPicker onColorChange={setTagColor} color={tagColor} />
			</Row>
			<div className={css(styleSheet.addTagFooter)}>
				<button
					className={css(styleSheet.addTagFooterAddButton, baseStyleSheet.ctaButton)}
					disabled={addTagMutation.isPending}
					onClick={onAddButtonClicked}
				>
					<span>Add</span>
				</button>
				{errorMessage ? (
					<span className={css(styleSheet.addTagFooterErrorMessage, baseStyleSheet.truncateText)} title={errorMessage}>
						{errorMessage}
					</span>
				) : null}
			</div>
			{addTagMutation.isPending ? <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' /> : null}
		</Column>
	);
}

const AddTagAsObserver = observer(_AddTag);
export const AddTag = inject(ModalChildComponentContextKey)(AddTagAsObserver);

export const AddTagModal = (props: IProps & { modalProps?: IModalProps }) => {
	const { modalProps = {}, ...restProps } = props;
	return (
		<Modal {...modalProps}>
			<AddTag {...restProps} />
		</Modal>
	);
};
